import { Dashboard } from '@/common/dashboard/Dashboard';
import { FilterContainer } from '@/common/dashboard/FilterContainer';
import { DimRearrangeButton } from '@/common/entityFramework/entityPerspective/support/DimRearrangeButton';
import { DropdownSelect } from '@/common/form/formFields/DropdownSelect';
import { EntitySelect } from '@/common/form/formFields/EntitySelect';
import { useNavStateSync } from '@/common/hooks/navState/useNavStateSync';
import { Spacer, SpacerLayout } from '@/common/layout/SpacerLayout';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { DirSaleTrendCard } from './cards/DirSaleTrendCard';

const metricOptions = [
  { value: 'SALES', displayName: 'Sales' },
  { value: 'QTY', displayName: 'Quantity' },
];

// All Entity Filter Field Definitions
const entityFilterFieldDefs = [
  {
    field: 'invPeriodExpr',
    displayName: 'Invoice Periods',
    entity: 'PERIOD',
    code: 'PERIOD',
    module: 'sc',
    multiple: true,
    parentFilterEntityModule: 'sc',
    parentFilterEntity: 'DIR_SALE_LINE',
    parentFilterFkField: 'invPeriodId',
  },
  {
    field: 'prodExpr',
    displayName: 'Products',
    entity: 'NDC11_PROD',
    code: 'NDC11_PROD',
    module: 'md',
    multiple: true,
    parentFilterEntityModule: 'sc',
    parentFilterEntity: 'DIR_SALE_LINE',
    parentFilterFkField: 'prodId',
  },
  {
    field: 'custExpr',
    displayName: 'Ship to Customers',
    entity: 'CUST',
    code: 'SHIP_CUST',
    module: 'md',
    multiple: true,
    parentFilterEntityModule: 'sc',
    parentFilterEntity: 'DIR_SALE_LINE',
    parentFilterFkField: 'shipCustId',
    hidden: true,
  },
  {
    field: 'soldCustExpr',
    displayName: 'Sold to Customers',
    entity: 'CUST',
    code: 'SOLD_CUST',
    module: 'md',
    multiple: true,
    parentFilterEntityModule: 'sc',
    parentFilterEntity: 'DIR_SALE_LINE',
    parentFilterFkField: 'soldCustId',
    hidden: true,
  },
];

// X-axis options for the dashboard charts
const xAxisOptions = [
  ...entityFilterFieldDefs.map((fieldDef) => ({ code: fieldDef.code, displayName: fieldDef.displayName, fieldDef })),
];

// All filters
const codeToFilter = Object.fromEntries(
  entityFilterFieldDefs.map((fieldDef) => [fieldDef.code, { displayName: fieldDef.displayName }]),
);

// Default filters
const defaultVisibleFilters = entityFilterFieldDefs
  .filter((fieldDef) => !fieldDef.hidden)
  .map((fieldDef) => fieldDef.code);

export const DirSaleFinDashboard = () => {
  const rsrc = useRsrc();

  const defaultFormValues = useRef();
  const [periodicityToPeriods, setPeriodicityToPeriods] = useState(false);
  const [visibleFilters, setVisibleFilters] = useNavStateSync('visibleFilters', defaultVisibleFilters);

  useEffect(() => {
    rsrc.data('sc', 'DIR_SALE_DASHBOARD_FILTERS_DATA_PLUGIN', {}).then((res) => {
      defaultFormValues.current = {
        periodicity: 'MON',
        asOfPeriodId: res.defaultAsOfPeriodId,
        metric: ['SALES'],
        prodExpr: null,
        custExpr: null,
        soldCustExpr: null,
        invPeriodExpr: ExprUtils.whereParams('id', res.invPeriodIds),
        invPeriodExprSelectedCount: res.invPeriodIds.length,
        invPeriodExprSelectedIds: res.invPeriodIds,
        invPeriodExprHeaderChecked: false,
        duration: 'YTD',
        xAxis: 'PERIOD',
      };
      setPeriodicityToPeriods(res.periodicityToPeriods);
    });
  }, []);

  // Saved nav state may reference filters that no longer exist
  const activeFieldDefs = useMemo(
    () =>
      visibleFilters.map((code) => entityFilterFieldDefs.find((fieldDef) => fieldDef.code === code)).filter(Boolean),
    [visibleFilters],
  );

  const resetDims = () => {
    setVisibleFilters(defaultVisibleFilters);
  };

  if (!periodicityToPeriods) return null;

  const augButtons = (buttons) => {
    buttons.unshift(
      <DimRearrangeButton
        key="filterRearrangeButton"
        setDims={setVisibleFilters}
        codeToDim={codeToFilter}
        activeDims={activeFieldDefs.map(({ code }) => ({ code }))}
      />,
    );
  };

  return (
    <Dashboard
      title="Direct Sales Financial Dashboard"
      titleIcon={fas.faFileInvoice}
      entity="DIR_SALE"
      defaultFormValues={defaultFormValues.current}
      augButtons={augButtons}
      onReset={resetDims}
    >
      <FilterContainer>
        <DropdownSelect
          fieldDef={{
            field: 'metric',
            displayName: 'Metric',
            multiSelect: true,
            values: metricOptions,
          }}
        />
        {activeFieldDefs.map((fieldDef) => (
          <EntitySelect key={fieldDef.field} fieldDef={{ ...fieldDef, variant: 'outlined' }} />
        ))}
      </FilterContainer>
      <SpacerLayout>
        <Spacer weights={[2, 1]}>
          <DirSaleTrendCard xAxisOptions={xAxisOptions} metricOptions={metricOptions} />
        </Spacer>
      </SpacerLayout>
    </Dashboard>
  );
};
